import type { RouteLocationNormalized } from 'vue-router';
import initRequestBody from './initRequestBody';
import { emptyModelData } from '~/components/distributor/emptyPageData';
import type {
  AutoFaqBlock,
  DynamicPageContext,
  LinkBlock,
  PageDataInterface,
  ResponseModelVideos,
} from '~/interfaces/api';
import { getArticles } from '~/api/global';
import {
  getLinkedModels,
  getModel,
  getModelMonitoring,
  modelsAccessories,
  modelsOffers,
  modelsOther,
  modelsQuestions,
  modelsReviews,
  modelsSpecification,
  modelsSpecificationBase,
  photoAllReviews,
} from '~/api/pageModel';
import type { PageDataModel } from '~/interfaces/app';

export const getNav = (pageData: PageDataInterface) => {
  const isCurrency =
    pageData.model?.price_info &&
    pageData.model?.offer_count &&
    !!(pageData.model?.price_info?.min || pageData.model?.price_info?.max);
  return [
    ['offers', pageData?.model?.offer_count || undefined],
    ['otzyvy', true],
    ['voprosy', true],
    ['specifications', pageData.baseSpec?.specifications?.length || undefined],
    ['videos', pageData?.model?.video_count || undefined],
    ['price-chart', pageData?.model?.offer_count || !isCurrency || undefined],
    ['accessories', pageData?.accessories?.length || undefined],
  ].reduce((sum, el) => (el[1] ? [...sum, el[0]] : sum), []);
};

export default async function initModelNew(
  context: DynamicPageContext,
  route: RouteLocationNormalized,
  store,
  blocks: AutoFaqBlock[]
) {
  const isReviews = route.path.endsWith('otzyvy/');
  const isQuestions = route.path.endsWith('voprosy/');

  // variables for models
  const model_id = context?.model_id | 0;
  const category_id = context?.category_id | 0;
  const region_id = store?.region?.id || 1;
  const isBot = store.isBot;
  // arguments for requests
  const modelArgs = [
    model_id,
    {
      region_id,
    },
  ];
  const modelsSpecificationBaseArgs = [model_id];

  const photoAllReviewsArgs = [
    model_id,
    {
      per_page: 20,
      page: 1,
      region_id,
      device: store.device || 1,
    },
  ];

  const reqBody = initRequestBody(route.query);
  const sortValue = route.query?.['products-sort'] || 'popularity';
  const modelsOffersArgs = [
    reqBody,
    model_id,
    {
      region_id,
      sort: sortValue,
      page: 1,
      per_page: 10,
      sem: store.sem || undefined,
      device: store?.device || undefined,
      split: store?.additional?.split || undefined,
      all: store?.debugPanelAll || undefined,
    },
  ];
  const modelsSimilarArgs = [
    model_id,
    {
      region_id,
      sem: store.sem || undefined,
      device: store.device || 1,
    },
  ];
  const paramReviewsQuestion = {
    per_page: 20,
    page: 1,
    external: true,
    region_id,
    device: store.device || 1,
    // антикеш
    tbp: `${model_id}${+new Date()}rew`,
  };

  const modelsSpecificationArgs = [model_id, isBot];
  const [
    model,
    baseSpec,
    pictures,
    offers,
    other,
    specifications,
    accessories,
    reviewsList,
    questions,
    linkedModels,
    articles,
    monitoring,
  ] = await Promise.all([
    // @ts-ignore
    getModel(...modelArgs),
    // @ts-ignore
    isBot ? modelsSpecificationBase(modelsSpecificationBaseArgs) : undefined,
    // @ts-ignore
    isBot ? photoAllReviews(...photoAllReviewsArgs) : undefined,
    // @ts-ignore
    modelsOffers(...modelsOffersArgs),
    // @ts-ignore
    isBot ? modelsOther(...modelsSimilarArgs) : undefined,
    // @ts-ignore
    isBot ? modelsSpecification(...modelsSpecificationArgs) : undefined,
    isBot ? modelsAccessories(category_id, region_id, store.sem) : undefined,
    isBot
      ? modelsReviews(model_id, { ...paramReviewsQuestion, external: false })
      : undefined,
    isBot ? modelsQuestions(model_id, paramReviewsQuestion) : undefined,
    isBot ? getLinkedModels(model_id) : undefined,
    isBot ? getArticles(model_id) : undefined,
    isBot
      ? getModelMonitoring(
          {
            region_id,
            sem: store.sem,
            device: store.device,
          },
          model_id
        )
      : undefined,
  ]);
  if (model?.cancelToken) return emptyModelData;
  const videos: ResponseModelVideos = model.videos;
  const brandPresets =
    blocks &&
    blocks.find((item: AutoFaqBlock | LinkBlock) => item.label === 'brands')
      ?.items;
  const itemsPresets = await blocks.find(
    (item: AutoFaqBlock | LinkBlock) => item?.label === 'preset'
  );

  const pageData = {
    model,
    baseSpec,
    presets: itemsPresets?.items || [],
    pictures,
    offers,
    specifications,
    videos,
    active: isReviews ? 'otzyvy' : isQuestions ? 'voprosy' : 'base',
    brandPresets,
    similar: [],
    other,
    accessories,
    reviewsList,
    questions,
    articles,
    linkedModels,
    monitoring,
  };

  return {
    ...pageData,
    navSections: getNav(<PageDataModel>pageData),
  };
}
